/**
 * v1.2 admin dashboard — API client.
 *
 * Fetch core (ApiError, ApiNetworkError, verb trio) lives in
 * @cortex/web-api; this module binds it to the "Admin API error" prefix and
 * keeps the /admin/* endpoint functions + response types. All calls go
 * through the same origin (admin.acelcortex.ai) so cookies stay
 * first-party. ApiError preserves the HTTP status; ApiNetworkError is
 * thrown when fetch itself fails (offline, DNS, etc.).
 *
 * 401 from any /admin/* endpoint signals "session expired or invalid". The
 * caller (App.tsx auth state machine) listens for this and bumps the user
 * back to the login screen with a small notice.
 */

import {
  ApiError,
  ApiNetworkError,
  getJson as getJsonShared,
  patchJson as patchJsonShared,
  postJson as postJsonShared,
} from '../../index.js';

export { ApiError, ApiNetworkError };

/** Baked into every ApiError.message thrown by this client. */
const ERROR_PREFIX = 'Admin API error';

async function postJson<T>(path: string, body: unknown): Promise<T> {
  return postJsonShared<T>(path, body, { errorPrefix: ERROR_PREFIX });
}

async function getJson<T>(path: string, options: { signal?: AbortSignal } = {}): Promise<T> {
  return getJsonShared<T>(path, { errorPrefix: ERROR_PREFIX, signal: options.signal });
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  return patchJsonShared<T>(path, body, { errorPrefix: ERROR_PREFIX });
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export type AuthStartResponse =
  | { status: 'sent'; expiresInSeconds: number }
  | { status: 'not_recognized' }
  | { status: 'rate_limited' };

export async function adminAuthStart(phone: string): Promise<AuthStartResponse> {
  return postJson<AuthStartResponse>('/admin/auth/start', { phone });
}

export type AuthVerifyResponse =
  | { status: 'verified' }
  | { status: 'wrong_code'; remainingAttempts?: number }
  | { status: 'expired_or_unknown' }
  | { status: 'locked' }
  | { status: 'revoked' };

export async function adminAuthVerify(phone: string, code: string): Promise<AuthVerifyResponse> {
  // /admin/auth/verify returns 401 with a structured body on failure; we want
  // to surface the .status field rather than throwing on 401.
  let res: Response;
  try {
    res = await fetch('/admin/auth/verify', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ phone, code }),
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  // 200 = verified; 401 = structured failure body; everything else = real error.
  if (res.status === 200 || res.status === 401) {
    return (await res.json()) as AuthVerifyResponse;
  }
  throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
}

export type AuthLoginResponse = { status: 'authenticated' };

export async function adminAuthLogin(
  username: string,
  password: string,
): Promise<AuthLoginResponse> {
  return postJson<AuthLoginResponse>('/admin/auth/login', { username, password });
}

export async function adminAuthLogout(): Promise<void> {
  try {
    await fetch('/admin/auth/logout', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Best-effort; clearing the local state happens regardless.
  }
}

/**
 * Unauthenticated probe — reports whether the current admin cookie is valid.
 * Best-effort: any failure is treated as unauthenticated so the login screen
 * is the safe default.
 */
export async function adminAuthStatus(): Promise<{ authenticated: boolean }> {
  try {
    return await getJson<{ authenticated: boolean }>('/admin/auth/status');
  } catch {
    return { authenticated: false };
  }
}

// ---------------------------------------------------------------------------
// Fleet
// ---------------------------------------------------------------------------

export interface TenantSummary {
  tenant: string;
  conversationsToday: number;
  lastInboundAt: string | null;
  outbound24h: { sent: number; failed: number; not_attempted: number };
  tokens24h: number;
  openTasksBuildium: number | null;
}

export interface EnvironmentStats {
  conversationsToday: number;
  lastInboundAt: string | null;
  outbound24h: { sent: number; failed: number; not_attempted: number };
  tokens24h: number;
  openTasksBuildium: number | null;
}

/**
 * Non-tenant environment (sandbox lane) surfaced on the fleet screen.
 * Present only when the demo container has CORTEX_SANDBOX_FLEET_URL set.
 * `stats` is null when the sandbox's HMAC-gated stats endpoint is
 * unreachable or refuses — health can still be shown.
 */
export interface EnvironmentSummary {
  name: string;
  kind: 'sandbox';
  url: string;
  status: 'healthy' | 'unreachable';
  buildSha: string | null;
  deployedBranch: string | null;
  stats: EnvironmentStats | null;
}

export interface FleetSummaryResponse {
  tenants: TenantSummary[];
  environments?: EnvironmentSummary[];
}

export async function fetchFleetSummary(): Promise<FleetSummaryResponse> {
  return getJson<FleetSummaryResponse>('/admin/fleet/summary');
}

// ---------------------------------------------------------------------------
// Executions
// ---------------------------------------------------------------------------

export type ExecutionStatus = 'running' | 'ok' | 'failed' | 'uncertain' | 'skipped';
export type ExecutionSource = 'tool_call' | 'queue_job' | 'provisioning' | 'run';
export type ExecutionClassification = 'error' | 'needs_review' | null;
export type ExecutionFreshness = 'fresh' | 'stale' | 'partial';
export type ExecutionPeriod = '24h' | '7d' | '30d';
export type ExecutionStatusFilter = 'all' | 'failed' | 'uncertain';

export interface ExecutionListItem {
  id: string;
  sourceId: string;
  tenant: string;
  environment: string;
  source: ExecutionSource;
  status: ExecutionStatus;
  classification: ExecutionClassification;
  triggerSummary: string;
  failedStep: string | null;
  errorPreview: string | null;
  occurredAt: string;
  timeBasis: 'exact' | 'provisioning_start_fallback';
  detailAvailable: boolean;
}

export interface ExecutionListPage {
  items: ExecutionListItem[];
  nextCursor: string | null;
  generatedAt: string;
  freshness: ExecutionFreshness;
  staleAgeSeconds: number | null;
  unavailableTenants: Array<{ tenant: string; reason: string }>;
}

export interface ExecutionDetail {
  item: ExecutionListItem;
  detail: Record<string, unknown>;
  generatedAt: string;
}

export interface ExecutionListParams {
  period: ExecutionPeriod;
  status: ExecutionStatusFilter;
  tenant?: string;
  q?: string;
  limit: 25 | 50;
  cursor?: string | null;
  refresh?: boolean;
}

export async function adminExecutionsList(params: ExecutionListParams): Promise<ExecutionListPage> {
  return getJson<ExecutionListPage>(`/admin/executions${executionQuery(params)}`);
}

export async function adminExecutionDetail(
  item: ExecutionListItem,
  signal?: AbortSignal,
): Promise<ExecutionDetail> {
  return getJson<ExecutionDetail>(
    `/admin/executions/${encodeURIComponent(item.tenant)}/${encodeURIComponent(
      item.source,
    )}/${encodeURIComponent(item.sourceId)}`,
    { signal },
  );
}

export function adminExecutionsCsvUrl(params: Omit<ExecutionListParams, 'cursor'>): string {
  return `/admin/executions/export.csv${executionQuery(params)}`;
}

function executionQuery(params: ExecutionListParams | Omit<ExecutionListParams, 'cursor'>): string {
  const search = new URLSearchParams();
  search.set('period', params.period);
  search.set('status', params.status);
  search.set('limit', String(params.limit));
  if (params.tenant) search.set('tenant', params.tenant);
  if (params.q) search.set('q', params.q);
  if ('cursor' in params && params.cursor) search.set('cursor', params.cursor);
  if (params.refresh) search.set('refresh', '1');
  return `?${search.toString()}`;
}

// ---------------------------------------------------------------------------
// Clients (v1.2 client provisioning)
// ---------------------------------------------------------------------------

export type ClientStatus = 'configuring' | 'bootstrapping' | 'live' | 'failed' | 'archived';
export type PmsProvider = 'buildium' | 'appfolio' | 'rentvine';

export interface ClientTenantPublic {
  onboarding?: {
    contactName: string | null;
    contactEmail: string | null;
    notification: string;
    notifiedAt: string | null;
  };
  slug: string;
  displayName: string;
  pmsProvider: PmsProvider;
  status: ClientStatus;
  smsEnabled: boolean;
  twilioFromNumber: string;
  twilioPhoneNumberSidTail: string;
  createdAt: string;
  errorText: string | null;
  hasPmGroup: boolean;
}

export interface AdminClientCreateInput {
  slug: string;
  displayName: string;
  pmsProvider: PmsProvider;
  smsEnabled: boolean;
  twilioAccountSid?: string;
  twilioAuthToken?: string;
  twilioFromNumber?: string;
  twilioPhoneNumberSid?: string;
  anthropicApiKey: string;
  knownCallersRaw?: string;
}

export async function adminClientsCreate(
  input: AdminClientCreateInput,
): Promise<{ client: ClientTenantPublic }> {
  return postJson<{ client: ClientTenantPublic }>('/admin/clients', input);
}

export async function adminClientsList(): Promise<{ clients: ClientTenantPublic[] }> {
  return getJson<{ clients: ClientTenantPublic[] }>('/admin/clients');
}

export async function adminClientsGet(slug: string): Promise<{ client: ClientTenantPublic }> {
  return getJson<{ client: ClientTenantPublic }>(`/admin/clients/${encodeURIComponent(slug)}`);
}

export async function adminClientsRetry(slug: string): Promise<{ client: ClientTenantPublic }> {
  return postJson<{ client: ClientTenantPublic }>(
    `/admin/clients/${encodeURIComponent(slug)}/retry`,
    {},
  );
}

// ---------------------------------------------------------------------------
// PM invites (v1.2 PR4a — platform bot Telegram identity binding)
// ---------------------------------------------------------------------------

export type UserRole = 'property_manager' | 'leasing_agent';

export interface AdminPmInviteCreateInput {
  tenantSlug: string;
  pmDisplayName: string;
  pmPhoneE164: string;
  pmEmail?: string;
  pmTelegramUsername?: string;
  userRole?: UserRole;
  /** Override default 7-day TTL. Range 1h–30d in seconds. */
  ttlSeconds?: number;
}

export interface AdminPmInviteCreateResponse {
  /** The t.me/AcelCortexBot?start=<token> URL admin sends to the PM. */
  url: string;
  /** Plaintext token — returned ONCE, treat as sensitive. */
  token: string;
  tenantSlug: string;
  tenantPmUserId: string;
  pmDisplayName: string;
  pmPhoneE164: string;
  userRole: UserRole;
  expiresAt: string;
  /**
   * Whether the invite SMS was delivered to `pmPhoneE164` from the demo
   * container's `TWILIO_FROM_NUMBER` (same number that sends OTPs). The
   * invite link is valid regardless — failures here just mean admin should
   * forward the link manually.
   */
  smsStatus: 'sent' | 'failed' | 'skipped';
  /** Present when `smsStatus === 'failed'`. Twilio error message; surfaced to admin so they know why. */
  smsError?: string;
}

export async function adminPmInviteCreate(
  input: AdminPmInviteCreateInput,
): Promise<AdminPmInviteCreateResponse> {
  return postJson<AdminPmInviteCreateResponse>('/admin/pm-invite', input);
}

// ---------------------------------------------------------------------------
// Setup invites (v1.2 PR4f — /setup wizard cookie-invite for Buildium/Email/Cal)
// ---------------------------------------------------------------------------

export interface AdminSetupInviteCreateInput {
  tenantSlug: string;
  pmDisplayName?: string;
  pmPhone: string;
  pmEmail: string;
  /** Override default 7-day TTL. Range 1h–30d in seconds. */
  ttlSeconds?: number;
}

export interface AdminSetupInviteCreateResponse {
  /** The <slug>.acelcortex.ai/setup#token=<TOKEN> URL admin sends to the PM. */
  url: string;
  /** Plaintext token — returned ONCE, treat as sensitive. */
  token: string;
  tenantSlug: string;
  pmDisplayName: string | null;
  pmPhone: string;
  pmEmail: string;
  expiresAt: string;
}

export async function adminSetupInviteCreate(
  input: AdminSetupInviteCreateInput,
): Promise<AdminSetupInviteCreateResponse> {
  return postJson<AdminSetupInviteCreateResponse>('/admin/setup-invite', input);
}

// ---------------------------------------------------------------------------
// PM bindings (v1.2 PR4a+ — confirmed platform-bot DMs per tenant)
// ---------------------------------------------------------------------------

export interface AdminPmBindingPublic {
  tenantPmUserId: string;
  /** Display name entered when the team member was invited, e.g. Madison. */
  displayName: string | null;
  /** Numeric chat_id stringified (bigint precision-safe). */
  chatId: string;
  telegramUsername: string | null;
  userRole: UserRole;
  confirmationState: 'pending' | 'confirmed' | 'denied';
  /** ISO-8601. */
  boundAt: string;
}

export async function adminPmBindingsList(slug: string): Promise<AdminPmBindingPublic[]> {
  const body = await getJson<{ bindings: AdminPmBindingPublic[] }>(
    `/admin/clients/${encodeURIComponent(slug)}/pm-bindings`,
  );
  return body.bindings;
}

export interface AdminInvoiceCardRecipientPublic {
  id: string;
  displayName: string | null;
  phoneE164: string | null;
  chatId: string | null;
  telegramUsername: string | null;
  userRole: UserRole;
  confirmationState: 'pending' | 'confirmed' | 'denied';
  receivesInvoiceCards: boolean;
  boundAt: string | null;
}

export async function adminInvoiceCardRecipientsList(
  slug: string,
): Promise<AdminInvoiceCardRecipientPublic[]> {
  const body = await getJson<{ recipients: AdminInvoiceCardRecipientPublic[] }>(
    `/admin/clients/${encodeURIComponent(slug)}/invoice-card-recipients`,
  );
  return body.recipients;
}

export async function adminInvoiceCardRecipientSet(
  slug: string,
  input: { tenantPmUserId: string; receivesInvoiceCards: boolean },
): Promise<AdminInvoiceCardRecipientPublic> {
  const body = await patchJson<{ recipient: AdminInvoiceCardRecipientPublic }>(
    `/admin/clients/${encodeURIComponent(slug)}/invoice-card-recipients`,
    input,
  );
  return body.recipient;
}

// ---------------------------------------------------------------------------
// PM group bindings + per-group memberships
// ---------------------------------------------------------------------------

export type PmGroupKind = 'leasing' | 'admin_accounting' | 'maintenance' | 'etransfers';

export interface AdminPmGroupBindingPublic {
  chatId: string;
  groupKind: PmGroupKind;
  title: string | null;
  boundAt: string;
  boundByTenantPmUserId: string | null;
}

export interface AdminPmGroupMembershipPublic {
  tenantPmUserId: string;
  groupKind: PmGroupKind;
  addedAt: string;
  addedByTenantPmUserId: string | null;
}

export async function adminPmGroupsGet(slug: string): Promise<{
  groups: AdminPmGroupBindingPublic[];
  memberships: AdminPmGroupMembershipPublic[];
}> {
  return getJson(`/admin/clients/${encodeURIComponent(slug)}/pm-groups`);
}

export async function adminPmGroupMembershipSet(input: {
  slug: string;
  tenantPmUserId: string;
  groupKind: PmGroupKind;
  enabled: boolean;
}): Promise<{ ok: true }> {
  return postJson(`/admin/clients/${encodeURIComponent(input.slug)}/pm-group-membership`, {
    tenantPmUserId: input.tenantPmUserId,
    groupKind: input.groupKind,
    enabled: input.enabled,
  });
}

// ---------------------------------------------------------------------------
// PM conversation (v1.2 PR7/PR8 — drill-down: bound PM's Telegram DM thread)
//
// The timeline merges stored chat messages with the agent's picker cards (which
// don't live in the messages table). A `message` item is a chat bubble; a
// `picker` item renders the Telegram inline keyboard, with the PM's tapped
// button flagged `pressed`.
// ---------------------------------------------------------------------------

export interface PmTimelineMessage {
  type: 'message';
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  /** ISO-8601. */
  sentAt: string;
  deliveryStatus: 'received' | 'sent' | 'failed' | 'not_attempted';
  /** Internal [SYSTEM] resume the PM never saw in Telegram — hidden by the UI. */
  system: boolean;
  actorDisplayName?: string | null;
  actorTelegramUsername?: string | null;
  pmGroupKind?: PmGroupKind | null;
}

export interface PmTimelinePickerButton {
  /** The button text the PM saw, e.g. "👥 All on lease (2 tenants)". */
  label: string;
  /** callback_data action token, e.g. "email", "0", "all", "confirm". */
  action: string;
  /** True for the button the PM actually tapped. */
  pressed: boolean;
}

export type PmPickerState = 'awaiting' | 'approved' | 'denied' | 'expired' | 'superseded';

export interface PmTimelinePicker {
  type: 'picker';
  id: string;
  /** pending_approvals.kind, e.g. "pm_tenant_pick". */
  approvalKind: string;
  /** The card body text the PM read above the buttons. */
  summary: string;
  state: PmPickerState;
  /** ISO-8601 — when the card was shown. */
  requestedAt: string;
  /** ISO-8601 — when the PM decided, or null if still awaiting/expired. */
  decidedAt: string | null;
  /** Human text of the tapped button (null for historical/free-text-superseded rows). */
  decidedLabel: string | null;
  /** Rows of buttons mirroring the Telegram inline keyboard. Empty for picker
   * kinds with no reconstruction (vendor/N4/N1) — render summary + state. */
  buttonRows: PmTimelinePickerButton[][];
}

export type AdminPmTimelineItem = PmTimelineMessage | PmTimelinePicker;

export async function adminPmConversationGet(
  slug: string,
  chatId: string,
  groupKind?: PmGroupKind | null,
): Promise<AdminPmTimelineItem[]> {
  const qs = groupKind ? `?groupKind=${encodeURIComponent(groupKind)}` : '';
  const body = await getJson<{ timeline: AdminPmTimelineItem[] }>(
    `/admin/clients/${encodeURIComponent(slug)}/pms/${encodeURIComponent(chatId)}/conversation${qs}`,
  );
  return body.timeline;
}

// ---------------------------------------------------------------------------
// Setup status (v1.2 — admin SetupStatusPanel: per-tenant card-completion)
// ---------------------------------------------------------------------------

export interface AdminSetupState {
  pms?: {
    provider: PmsProvider;
    setupMode: 'self_service' | 'operations_only';
    label: string;
    description: string;
    connection: {
      status: 'missing' | 'disabled' | 'active' | 'error';
      connected: boolean;
      verifiedCapabilities: string[];
      enabledCapabilities: string[];
      grantedCapabilities: string[];
      safeSummary: Record<string, string | number | boolean | null>;
      lastVerifiedAt: string | null;
      lastSyncAt: string | null;
      safeError: string | null;
    };
  };
  cards: {
    pms?: { connected: boolean };
    buildium: { connected: boolean };
    email: { connected: boolean };
    calendar: { connected: boolean };
    drive: { connected: boolean };
    phone: { connected: boolean };
  };
  demo: { completed: boolean };
  /** Container-level misconfiguration hint: SETTINGS_MASTER_KEY is unset
   * so all cards report as awaiting regardless of actual state. */
  settingsKeyMissing: boolean;
}

export async function adminSetupState(slug: string): Promise<AdminSetupState> {
  return getJson<AdminSetupState>(`/admin/clients/${encodeURIComponent(slug)}/setup-state`);
}

// ---------------------------------------------------------------------------
// Email routing (tenant fallback forwarding + tracked reply example)
// ---------------------------------------------------------------------------

export interface AdminEmailRoutingSettings {
  address: string;
  forwardTo: string | null;
  configured: boolean;
  trackedReplyExample: string;
  deprecatedAddresses: string[];
}

export async function adminEmailRoutingGet(slug: string): Promise<AdminEmailRoutingSettings> {
  return getJson<AdminEmailRoutingSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/email-routing`,
  );
}

export async function adminEmailRoutingSet(
  slug: string,
  input: { forwardTo: string },
): Promise<AdminEmailRoutingSettings> {
  return patchJson<AdminEmailRoutingSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/email-routing`,
    input,
  );
}

// ---------------------------------------------------------------------------
// Site login recovery email (shared-password reset recipient)
// ---------------------------------------------------------------------------

export interface AdminSiteRecoverySettings {
  siteRecoveryEmail: string | null;
  cortexEmailForwardTo: string | null;
  effective: string | null;
}

export async function adminSiteRecoveryGet(slug: string): Promise<AdminSiteRecoverySettings> {
  return getJson<AdminSiteRecoverySettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/site-recovery`,
  );
}

export async function adminSiteRecoverySet(
  slug: string,
  input: { siteRecoveryEmail: string | null },
): Promise<AdminSiteRecoverySettings> {
  return patchJson<AdminSiteRecoverySettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/site-recovery`,
    input,
  );
}

// ---------------------------------------------------------------------------
// N4 template (v1.x bulk-N4-prepare — PM-signed PDF persisted per tenant)
// ---------------------------------------------------------------------------

export type AdminN4TemplateStatus =
  | {
      status: 'uploaded';
      fileName: string;
      uploadedAt: string | null;
      sha256: string | null;
      sizeBytes: number;
    }
  | { status: 'not_uploaded' };

export async function adminN4TemplateStatus(slug: string): Promise<AdminN4TemplateStatus> {
  return getJson<AdminN4TemplateStatus>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/n4-template`,
  );
}

export type AdminN4TemplateUploadResponse =
  | {
      status: 'uploaded';
      fileName: string;
      uploadedAt: string;
      sha256: string;
      fieldCount: number;
      signatureFlattened: boolean;
      signDateFillable: boolean;
      fillableTenantFields: string[];
      remainingPmFields: string[];
    }
  | {
      status: 'validation_failed';
      reason: string;
      message: string;
      missingFields?: string[];
    };

export async function adminN4TemplateUpload(
  slug: string,
  file: File,
  opts: { requirePreSigned?: boolean } = {},
): Promise<AdminN4TemplateUploadResponse> {
  const fd = new FormData();
  fd.append('template', file, file.name);
  const qs = opts.requirePreSigned ? '?requirePreSigned=1' : '';
  let res: Response;
  try {
    res = await fetch(`/admin/clients/${encodeURIComponent(slug)}/settings/n4-template${qs}`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (res.status === 200 || res.status === 422) {
    return (await res.json()) as AdminN4TemplateUploadResponse;
  }
  throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
}

export async function adminN4TemplateDelete(slug: string): Promise<{ status: 'cleared' }> {
  let res: Response;
  try {
    res = await fetch(`/admin/clients/${encodeURIComponent(slug)}/settings/n4-template`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
  }
  return (await res.json()) as { status: 'cleared' };
}

// ---------------------------------------------------------------------------
// N1 template (v1.x bulk-N1-prepare — PM-signed PDF persisted per tenant)
// Mirror of the N4 trio above with the URL path swapped to /n1-template.
// ---------------------------------------------------------------------------

export type AdminN1TemplateStatus = AdminN4TemplateStatus;

export async function adminN1TemplateStatus(slug: string): Promise<AdminN1TemplateStatus> {
  return getJson<AdminN1TemplateStatus>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/n1-template`,
  );
}

export type AdminN1TemplateUploadResponse = AdminN4TemplateUploadResponse;

export async function adminN1TemplateUpload(
  slug: string,
  file: File,
  opts: { requirePreSigned?: boolean } = {},
): Promise<AdminN1TemplateUploadResponse> {
  const fd = new FormData();
  fd.append('template', file, file.name);
  const qs = opts.requirePreSigned ? '?requirePreSigned=1' : '';
  let res: Response;
  try {
    res = await fetch(`/admin/clients/${encodeURIComponent(slug)}/settings/n1-template${qs}`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (res.status === 200 || res.status === 422) {
    return (await res.json()) as AdminN1TemplateUploadResponse;
  }
  throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
}

export async function adminN1TemplateDelete(slug: string): Promise<{ status: 'cleared' }> {
  let res: Response;
  try {
    res = await fetch(`/admin/clients/${encodeURIComponent(slug)}/settings/n1-template`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
  }
  return (await res.json()) as { status: 'cleared' };
}

// ---------------------------------------------------------------------------
// Per-tenant agent model switcher — active profile is read fresh every agent turn.
// API keys are write-only: responses expose only keyConfigured.
// ---------------------------------------------------------------------------

export type AdminAgentModelProfileId =
  | 'anthropic-haiku-4-5'
  | 'kimi-k2.7-code'
  | 'openrouter-minimax-m3';

export interface AdminAgentModelProfile {
  id: AdminAgentModelProfileId;
  label: string;
  provider: 'anthropic' | 'kimi' | 'openrouter';
  model: string;
  baseUrl: string | null;
  keyConfigured: boolean;
  active: boolean;
}

export interface AdminAgentModelSettings {
  activeProfile: AdminAgentModelProfileId;
  profiles: AdminAgentModelProfile[];
  updatedAt: string;
}

export interface AdminAgentModelPatchInput {
  anthropicApiKey?: string;
  activeProfile?: AdminAgentModelProfileId;
  kimiApiKey?: string | null;
  kimiBaseUrl?: string;
  kimiModel?: string;
  openRouterApiKey?: string | null;
  openRouterBaseUrl?: string;
  openRouterModel?: string;
}

export async function adminAgentModelGet(slug: string): Promise<AdminAgentModelSettings> {
  return getJson<AdminAgentModelSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/agent-model`,
  );
}

export async function adminAgentModelSet(
  slug: string,
  input: AdminAgentModelPatchInput,
): Promise<AdminAgentModelSettings> {
  return patchJson<AdminAgentModelSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/agent-model`,
    input,
  );
}

export async function adminAgentModelTest(
  slug: string,
  input: { profile?: AdminAgentModelProfileId } & Omit<AdminAgentModelPatchInput, 'activeProfile'>,
): Promise<{ ok: boolean; profile: AdminAgentModelProfileId; error?: string }> {
  return postJson<{ ok: boolean; profile: AdminAgentModelProfileId; error?: string }>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/agent-model/test`,
    input,
  );
}

// ---------------------------------------------------------------------------
// Per-tenant feature flags (v1.3) — admin-toggleable runtime switches stored in
// tenant_settings; read fresh every agent turn so toggles apply with no restart.
// ---------------------------------------------------------------------------

export interface AdminFeatureFlags {
  /** NOE flow is Buildium-only (no Email Draft channel). */
  noeBuildiumOnly: boolean;
  /** NOE flow is Email-only (no Buildium Template channel). */
  noeEmailOnly: boolean;
  /** Post-send NOE calendar prompt. */
  noeCalendarPromptEnabled: boolean;
  /** Day-of NOE email reminders for email-channel NOEs. */
  noeEmailReminderEnabled: boolean;
  /** Invoice intake + PM review cards enabled. */
  invoicingEnabled: boolean;
  /** Conservative generation-2 email invoice-ness gate. */
  invoiceIntakeGateEnabled: boolean;
  /** Fetch invoice PDFs behind allowlisted Square/QuickBooks links. Defaults off. */
  invoiceLinkFetchEnabled: boolean;
  /** AP1 task automation: auto-link high-confidence Buildium tasks or create a safe replacement. */
  apTaskAutomationEnabled: boolean;
  /** Restore eligible unpaid AP2 rows to AP1. Intended for sandbox testing. */
  apPostedUndoEnabled: boolean;
  /** Send the exact AP2 vendor-copy CSV snapshot by email. */
  apVendorCopyEmailEnabled: boolean;
  /** Primary-owner AP1 reprocess action. Defaults off for staged rollout. */
  apInvoiceReprocessEnabled: boolean;
  /** Route internal-worker labor invoices (roster technician, $0 vendor) into tech billing. */
  internalTechInvoicesEnabled: boolean;
  /** Helper labor lines on tech jobs (per-technician payout fan-out at post). */
  techHelperLaborEnabled: boolean;
  /** Mirror current technician pay/status into ops-routed Google Sheets. */
  technicianSheetStatusWritebackEnabled: boolean;
  /** Current-truth technician Work/Reimbursement row projector. Guarded ops activation only. */
  technicianSheetRowProjectionEnabled: boolean;
  /** Tenant-dashboard self-serve Configure writes (identity, Buildium IDs, safe flags). */
  configureSelfServeEnabled: boolean;
  /** Generic Task Progress Report intake normalizer. Detailed mappings are ops-managed. */
  taskProgressReportIntakeEnabled: boolean;
  /** Seasonal inspection form intake normalizer. Detailed mappings are ops-managed. */
  seasonalInspectionIntakeEnabled: boolean;
  /** Gmail e-transfer intake + Telegram approval queue enabled. */
  etransfersEnabled: boolean;
  etransferTelegramNotificationsEnabled: boolean;
  /** Trusted-payer e-transfer auto-posting enabled. Defaults off. */
  etransferAutoPostEnabled: boolean;
  /** Shadow e-transfer pilot: never posts while still recording matches and confirmations. */
  etransferShadowModeEnabled: boolean;
  /** Primary-owner posted e-transfer undo/reversal action. Defaults off. */
  etransferPaymentUndoEnabled: boolean;
  /** Guarded Buildium owner-contribution quick-deposit posting. Defaults off. */
  etransferOwnerContributionPostingEnabled: boolean;
  /** Backfill a bounded outage gap after either Gmail intake lane reconnects. */
  gmailReconnectBackfillEnabled: boolean;
  /** Legal test mode: N1/N4 run repeatably without real Buildium/tenant side effects. */
  legalTestMode: boolean;
  /** Display-only Maintenance lane visibility on the client owner dashboard. */
  laneMaintenanceEnabled: boolean;
  /** Display-only Accounting lane visibility on the client owner dashboard. */
  laneAccountingEnabled: boolean;
  /** Display-only Leasing lane visibility on the client owner dashboard. */
  laneLeasingEnabled: boolean;
  /** Read-only Accounting profitability for this client (on by default). */
  etransferForwarderDkimRequired: boolean;
  etransferForwarderSpfRequired: boolean;
  etransferSignedForwardingEnabled: boolean;
  accountingProfitabilityEnabled: boolean;
  /** Home's number tiles link to the page behind each figure (on by default). */
  homeKpiLinksEnabled: boolean;
  /** Vendor-list split email/phone columns and sender-email invoice matching. */
  vendorContactColumnsEnabled: boolean;
}

export type AdminFeatureFlag = keyof AdminFeatureFlags;

export const ADMIN_FEATURE_FLAG_KEYS = [
  'noeBuildiumOnly',
  'noeEmailOnly',
  'noeCalendarPromptEnabled',
  'noeEmailReminderEnabled',
  'invoicingEnabled',
  'invoiceIntakeGateEnabled',
  'invoiceLinkFetchEnabled',
  'apTaskAutomationEnabled',
  'apPostedUndoEnabled',
  'apVendorCopyEmailEnabled',
  'apInvoiceReprocessEnabled',
  'internalTechInvoicesEnabled',
  'techHelperLaborEnabled',
  'technicianSheetStatusWritebackEnabled',
  'technicianSheetRowProjectionEnabled',
  'configureSelfServeEnabled',
  'taskProgressReportIntakeEnabled',
  'seasonalInspectionIntakeEnabled',
  'etransfersEnabled',
  'etransferTelegramNotificationsEnabled',
  'etransferAutoPostEnabled',
  'etransferShadowModeEnabled',
  'etransferPaymentUndoEnabled',
  'etransferOwnerContributionPostingEnabled',
  'gmailReconnectBackfillEnabled',
  'legalTestMode',
  'laneMaintenanceEnabled',
  'laneAccountingEnabled',
  'laneLeasingEnabled',
  'etransferForwarderDkimRequired',
  'etransferForwarderSpfRequired',
  'etransferSignedForwardingEnabled',
  'accountingProfitabilityEnabled',
  'homeKpiLinksEnabled',
  'vendorContactColumnsEnabled',
] as const satisfies readonly AdminFeatureFlag[];

type AssertNever<T extends never> = T;
type _AllAdminFeatureFlagsAreValidated = AssertNever<
  Exclude<AdminFeatureFlag, (typeof ADMIN_FEATURE_FLAG_KEYS)[number]>
>;

function requireAdminFeatureFlags(value: unknown): AdminFeatureFlags {
  if (
    typeof value !== 'object' ||
    value === null ||
    !ADMIN_FEATURE_FLAG_KEYS.every(
      (key) => typeof (value as Record<string, unknown>)[key] === 'boolean',
    )
  ) {
    throw new ApiError(
      502,
      JSON.stringify({ error: 'invalid_feature_flags_response' }),
      ERROR_PREFIX,
    );
  }
  return value as AdminFeatureFlags;
}

export async function adminFeatureFlagsGet(slug: string): Promise<AdminFeatureFlags> {
  const body = await getJson<{ flags: unknown }>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/feature-flags`,
  );
  return requireAdminFeatureFlags(body.flags);
}

export async function adminFeatureFlagsSet(
  slug: string,
  input: { flag: AdminFeatureFlag; enabled: boolean },
): Promise<AdminFeatureFlags> {
  const body = await patchJson<{ flags: unknown }>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/feature-flags`,
    input,
  );
  return requireAdminFeatureFlags(body.flags);
}

// ---------------------------------------------------------------------------
// Owner dashboard admin controls (nav/settings epic)
// ---------------------------------------------------------------------------

export interface AdminJurisdiction {
  country: string | null;
  region?: string | null;
  county?: string | null;
}

export interface AdminNoticeFormPublic {
  noticeType: string;
  name: string;
  jurisdictionCountry: string;
  jurisdictionRegion: string | null;
  jurisdictionCounty: string | null;
  formVersion: string;
  effectiveDate: string;
}

export interface AdminJurisdictionSettings {
  jurisdiction: AdminJurisdiction;
  forms: AdminNoticeFormPublic[];
}

export async function adminJurisdictionGet(slug: string): Promise<AdminJurisdictionSettings> {
  return getJson<AdminJurisdictionSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/jurisdiction`,
  );
}

export async function adminJurisdictionSet(
  slug: string,
  jurisdiction: AdminJurisdiction,
): Promise<AdminJurisdictionSettings> {
  return patchJson<AdminJurisdictionSettings>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/jurisdiction`,
    jurisdiction,
  );
}

export type AdminAccountingNotificationMode = 'as_received' | 'daily_digest';

export interface AdminOperationalConfigView {
  company: string;
  status: Record<string, string>;
  textNumbers: { residents: string; vendors: string };
  maintenanceWorkflow: Array<{
    key: string;
    label: string;
    channels: { pms?: boolean; text?: boolean; email?: boolean };
    sourceOfTruth: string;
    sheetLink: string | null;
    sharedEmail: string | null;
  }>;
  accounting: {
    forwardTo: string;
    mode: AdminAccountingNotificationMode;
    days: string[];
    localTime: string;
  };
  features: Record<string, boolean | string | number | null>;
  jurisdiction: AdminJurisdiction | null;
  noticeForms: AdminNoticeFormPublic[];
}

export interface AdminOperationalConfigPatch {
  vendorSmsFromNumber?: string | null;
  maintenanceWorkflowMatrix?: Record<string, unknown> | null;
  accountingNotificationMode?: AdminAccountingNotificationMode;
  accountingNotificationDays?: string[] | null;
  accountingNotificationLocalTime?: string;
  utilitySplitEnabled?: boolean;
  utilityGlAccountId?: number | null;
}

export async function adminOperationalConfigGet(slug: string): Promise<AdminOperationalConfigView> {
  return getJson<AdminOperationalConfigView>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/operational-config`,
  );
}

export async function adminOperationalConfigSet(
  slug: string,
  patch: AdminOperationalConfigPatch,
): Promise<AdminOperationalConfigView> {
  return patchJson<AdminOperationalConfigView>(
    `/admin/clients/${encodeURIComponent(slug)}/settings/operational-config`,
    patch,
  );
}

export interface AdminBillingPaymentPublic {
  id: string;
  paidAt: string;
  amountCents: number;
  billedUnits: number | null;
  perUnitCents: number | null;
  fixedMonthlyCents?: number | null;
  source?: 'manual' | 'stripe';
  entryType?: 'payment' | 'refund';
  note: string | null;
  receiptUrl: string | null;
  invoiceUrl?: string | null;
}

export interface AdminBillingView {
  configured: boolean;
  planType: 'per_unit' | 'fixed_monthly' | 'custom';
  currency: 'usd';
  billedUnits: number;
  perUnitCents: number | null;
  fixedMonthlyCents: number | null;
  nextBillingDate: string | null;
  nextPaymentAmountCents: number | null;
  billedUnitsSnapshotAt: string | null;
  currentPmsUnits: number;
  unitsSyncedAt: string | null;
  agreement: { available: boolean; url: string | null; fileName: string | null };
  payments: AdminBillingPaymentPublic[];
  billingContactEmail: string | null;
  billingContactTelegramChatId: number | null;
  upcomingDaysBefore: number;
  overdueDays: number[];
  graceDays: number;
  stripeBillingEnabled: boolean;
  stripeBillingRolloutAllowed: boolean;
  tenantProjectionUnavailable: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  billingStatus: 'active' | 'past_due' | 'suspended';
  billingStatusVerifiedAt: string | null;
  billingStatusReplicatedAt: string | null;
}

export async function adminBillingGet(slug: string): Promise<AdminBillingView> {
  return getJson<AdminBillingView>(`/admin/clients/${encodeURIComponent(slug)}/billing`);
}

export async function adminBillingSet(
  slug: string,
  patch: {
    planType?: 'per_unit' | 'fixed_monthly' | 'custom';
    perUnitCents?: number | null;
    fixedMonthlyCents?: number | null;
    billedUnits?: number;
    nextBillingDate?: string | null;
    billingContactEmail?: string | null;
    billingContactTelegramChatId?: number | null;
    stripeBillingEnabled?: boolean;
  },
): Promise<AdminBillingView> {
  return patchJson<AdminBillingView>(`/admin/clients/${encodeURIComponent(slug)}/billing`, patch);
}

export async function adminBillingAgreementUpload(
  slug: string,
  file: File,
): Promise<AdminBillingView> {
  const form = new FormData();
  form.append('agreement', file);
  return fetchFormJson<AdminBillingView>(
    `/admin/clients/${encodeURIComponent(slug)}/billing/agreement`,
    'PUT',
    form,
  );
}

export async function adminBillingPaymentCreate(
  slug: string,
  input: {
    paidAt: string;
    amountCents: number;
    billedUnits: number;
    perUnitCents: number;
    receipt: File;
    note?: string;
  },
): Promise<AdminBillingView> {
  const form = new FormData();
  form.append('paidAt', input.paidAt);
  form.append('amountCents', String(input.amountCents));
  form.append('billedUnits', String(input.billedUnits));
  form.append('perUnitCents', String(input.perUnitCents));
  form.append('receipt', input.receipt);
  if (input.note) form.append('note', input.note);
  return fetchFormJson<AdminBillingView>(
    `/admin/clients/${encodeURIComponent(slug)}/billing/payments`,
    'POST',
    form,
  );
}

async function fetchFormJson<T>(path: string, method: 'POST' | 'PUT', body: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { method, body, credentials: 'same-origin' });
  } catch (err) {
    throw new ApiNetworkError(err);
  }
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ''), ERROR_PREFIX);
  return (await res.json()) as T;
}

export interface AdminInvoiceUsage {
  from: string;
  to: string;
  clients: Array<{
    slug: string;
    displayName: string;
    status: 'ok' | 'unavailable' | 'unsupported';
    count: number | null;
  }>;
}

export async function adminInvoiceUsageGet(
  options: { signal?: AbortSignal } = {},
): Promise<AdminInvoiceUsage> {
  return getJson<AdminInvoiceUsage>('/admin/invoice-usage', options);
}

/** Safe control-plane mailbox progress; secret identities and provider metadata stay internal. */
export type AdminManagedEmailView =
  | { enrolled: false; revision: 0; infrastructureReady: boolean }
  | {
      enrolled: true;
      revision: number;
      infrastructureReady: boolean;
      initialSetup?:
        | 'waiting_for_mailbox'
        | 'waiting_for_infrastructure'
        | 'infrastructure_started'
        | 'needs_attention'
        | null;
      primaryAddress: string;
      invoiceRecipient: string;
      etransferRecipient: string;
      phase:
        | 'directory_pending'
        | 'stage_pending'
        | 'delivery_needed'
        | 'delivery_pending'
        | 'activation_pending'
        | 'active_preserved'
        | 'blocked'
        | 'outcome_unknown'
        | 'preserved';
      errorCode: string | null;
      pending: boolean;
      sendUnresolved: boolean;
      canSendTest: boolean;
      profileVerifiedAt: string | null;
      invoiceDeliveryVerifiedAt: string | null;
      etransferDeliveryVerifiedAt: string | null;
      lastCheckedAt: string | null;
      secureHandoff: 'not_recorded_here';
      licenseAssignment: 'not_recorded_here';
    };
export function adminManagedEmailGet(
  slug: string,
  signal?: AbortSignal,
): Promise<AdminManagedEmailView> {
  return getJson(`/admin/clients/${encodeURIComponent(slug)}/managed-email`, { signal });
}
export function adminManagedEmailAction(
  slug: string,
  revision: number,
  action: 'continue' | 'delivery-test',
): Promise<AdminManagedEmailView> {
  return postJson(`/admin/clients/${encodeURIComponent(slug)}/managed-email/${action}`, {
    revision,
  });
}
